/**
 * UX2 — deterministic primary CTA for Sales Quotation workspace.
 * Uses FE policy mirror; never bypasses backend authority.
 */
import type { Quotation } from '@/services/quotation.service';
import {
  canConvertQuotationToSalesOrder,
  canCreateQuotationRevision,
  canSendQuotationToCustomer,
} from './quotationRevisionPolicy';
import { resolveFamilyRole } from './quotationRevisionIntelligence';

export type QuotationPrimaryCtaKey =
  | 'submit_approval'
  | 'send'
  | 'accept'
  | 'revise'
  | 'create_contract_review'
  | 'open_contract_review'
  | 'convert_so'
  | 'view_so'
  | 'open_current'
  | 'edit'
  | 'none';

export type QuotationPrimaryCta = {
  key: QuotationPrimaryCtaKey;
  label: string;
  disabled?: boolean;
  disabledReason?: string;
};

function norm(v: unknown): string {
  return String(v ?? '').trim().toLowerCase();
}

export function resolveQuotationPrimaryCta(input: {
  quotation: Quotation;
  universalApprovalPending?: boolean;
  universalApprovalApproved?: boolean;
  hasActiveContractReview?: boolean;
  hasApprovedContractReview?: boolean;
  isTradingRental?: boolean;
  currentOfferId?: string | null;
}): QuotationPrimaryCta {
  const { quotation } = input;
  const role = resolveFamilyRole(quotation);
  const status = norm(quotation.status);
  const approval = norm(quotation.approval_status);

  if (role === 'superseded' && input.currentOfferId && input.currentOfferId !== quotation.id) {
    return { key: 'open_current', label: 'Open Current Revision' };
  }

  if (quotation.is_converted_to_sales_order && quotation.sales_order_id) {
    return { key: 'view_so', label: 'View Customer Order' };
  }

  if (status === 'accepted' && role === 'accepted') {
    if (input.hasActiveContractReview) {
      return { key: 'open_contract_review', label: 'Open Contract Review' };
    }
    if (!input.isTradingRental) {
      return { key: 'create_contract_review', label: 'Proceed to Contract Review' };
    }
    const convert = canConvertQuotationToSalesOrder(quotation);
    if (convert.ok) {
      return { key: 'convert_so', label: 'Convert to Customer Order' };
    }
    return { key: 'none', label: 'Accepted', disabled: true, disabledReason: convert.reason };
  }

  if (status === 'sent' && quotation.is_current_submitted) {
    return { key: 'accept', label: 'Mark Customer Accepted' };
  }

  const sendGate = canSendQuotationToCustomer(quotation);
  if (sendGate.ok) {
    return { key: 'send', label: 'Send to Customer' };
  }

  const reviseGate = canCreateQuotationRevision(quotation);
  if (reviseGate.ok && (status === 'sent' || status === 'accepted')) {
    return { key: 'revise', label: 'Create Revision' };
  }

  if (status === 'draft') {
    if (approval === 'pending' && !input.universalApprovalPending) {
      return { key: 'submit_approval', label: 'Submit for Approval' };
    }
    if (approval === 'approved' || input.universalApprovalApproved) {
      return { key: 'send', label: 'Send to Customer' };
    }
    if (quotation.approval_instance_id && input.universalApprovalPending) {
      return { key: 'none', label: 'Approval Pending', disabled: true };
    }
    return { key: 'edit', label: 'Edit Quotation' };
  }

  if (reviseGate.ok) {
    return { key: 'revise', label: 'Create Revision' };
  }

  return { key: 'none', label: 'View Only', disabled: true, disabledReason: reviseGate.reason };
}
